import { AnimatePresence, motion } from 'framer-motion';
import { SnackbarProvider } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useOutlet } from 'react-router-dom';
import VConsole from 'vconsole';
import PWAInstalHeader from '../../components/PWAInstalHeader/PWAInstalHeader';
import AppConfig from '../../config';
import useBackButton from '../../hooks/useBackButton';
import { PageKeyProvider } from '../../hooks/usePageContext';
import HelperService from '../../services/HelperService';
import StorageService from '../../services/StorageService';
import TelegramWebApiService from '../../services/TelegramWebApiService';
import TypesService from '../../services/TypesService';
import WebSocketService from '../../services/WebSocketService';
import { appSlice } from '../../store/slices';
import RootModals from '../RootModal/RootModals';
import './App.scss';
import useSwipeDetection from './useSwipeDetection';

const { isEnableDevTools, routes } = AppConfig;
const isDevEnv = process.env.NODE_ENV === 'development';
const routesForBackButton = Object.values(routes).filter(route => !TypesService.BackButtonExceptions.includes(route));

function App() {
	const [items, setItems] = useState([]);

	const installPWARef = useRef(null);
	const isFirstRender = useRef(true);
	const prevIdx = useRef(window.history.state?.idx ?? 0);
	const outlet = useOutlet();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	useBackButton(routesForBackButton.includes(location.pathname));
	const needAnimation = !useSwipeDetection();

	const token = useSelector(state => state.app.token);
	const version = useSelector(state => state?.app?.version);

	const currentIdx = window.history.state?.idx ?? 0;
	const goingBack = currentIdx < prevIdx.current;
	const direction = isFirstRender.current ? 'start' : goingBack ? 'backward' : 'forward';

	const initTgData = () => {
		dispatch(appSlice.actions.setExternalId(TelegramWebApiService.TelegramWebApi.initDataUnsafe.user.id));
		dispatch(appSlice.actions.setInitDataTG(TelegramWebApiService.TelegramWebApi.initData));
	};

	const initStoreData = () => {
		StorageService.UpdateQuotes();
		StorageService.UpdateBillingBalance();
		StorageService.UpdateUserCurrentPermissions();
		StorageService.UpdateDevices();
		StorageService.Initialize();
	};

	useEffect(() => {
		StorageService.GetDeviceInfo();
	}, []);

	useEffect(() => {
		TelegramWebApiService?.Ready();
		TelegramWebApiService?.InitHeader();
		TelegramWebApiService?.Expand();
		TelegramWebApiService?.InitHeader();

		// TelegramWebApiService?.InitTheme();
		TelegramWebApiService?.SetLanguage();
		TelegramWebApiService?.LockOrientationLandscape();
		TelegramWebApiService?.DisableVerticalSwipes();

		if (TypesService.DesktopPlatforms.includes(TelegramWebApiService.TelegramWebApi.platform)) {
			TelegramWebApiService?.Expand();
		} else TelegramWebApiService?.RequestFullscreen();

		// const handleBlur = async e => {
		// 	const isMobile = /iPhone|iPad|Android/.test(navigator.userAgent);

		// 	if (isMobile) {
		// 		await HelperService.Sleep(250);
		// 		window.scrollTo({ top: 0, behavior: 'smooth' });
		// 		// document.documentElement.style.removeProperty('height');
		// 		await HelperService.Sleep(50);
		// 		document.documentElement.style.height = `${window.visualViewport?.height}px`;
		// 	}
		// };

		// let activeInput = null;

		// const inputs = document.querySelectorAll('input', 'textarea');
		// inputs.forEach(input => input.addEventListener('blur', handleBlur));
		// inputs.forEach(input => input.addEventListener('focusin', handleBlur));

		return () => {
			// inputs.forEach(input => input.removeEventListener('blur', handleBlur));
			// inputs.forEach(input => input.removeEventListener('focusin', handleBlur));
		};
	});

	useEffect(() => {
		document.title = AppConfig.name;
	}, []);

	useEffect(() => {
		if (AppConfig.creditCurrencyIcon) {
			const link = document.querySelector("link[rel~='icon']");

			if (link) {
				link.href = AppConfig.creditCurrencyIcon;
			}
		}
	}, []);

	useEffect(() => {
		if (isEnableDevTools) new VConsole();
		console.log('navigator.userAgent: ', navigator.userAgent);

		console.log('TelegramWebApiService.TelegramWebApi.platform', TelegramWebApiService.TelegramWebApi.platform);

		initTgData();

		console.log('App version: ', version);

		// было реализовано для инпутов в модальном окне
		// TelegramWebApiService?.OnThemeChange();
		// const bodyHeight = async () => {
		// 	await HelperService.Sleep(150);
		// 	document.documentElement.style.height = `${window.visualViewport?.height}px`;
		// 	await HelperService.Sleep(350);
		// 	const focusedElement = document.activeElement;
		// 	if (focusedElement && focusedElement.tagName === 'INPUT') {
		// 		focusedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
		// 		await HelperService.Sleep(150);
		// 		window.scrollTo({ top: 0, behavior: 'auto' });
		// 	} else {
		// 		window.scrollTo({ top: 0, behavior: 'auto' });
		// 	}
		// };
		const bodyHeight = async () => {
			await HelperService.Sleep(150);
			document.body.style.height = `${window.visualViewport?.height}px`;
			await HelperService.Sleep(350);
			const focusedElement = document.activeElement;

			// window.scrollTo({ top: 0, behavior: 'auto' });

			if (focusedElement && focusedElement.tagName === 'INPUT') {
				// await HelperService.Sleep(250);
				focusedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
			}

			await HelperService.Sleep(250);
			window.scrollTo({ top: 0, behavior: 'auto' });
		};

		window.visualViewport?.addEventListener('resize', bodyHeight);
		bodyHeight();

		return () => {
			window.visualViewport?.removeEventListener('resize', bodyHeight);
			// TelegramWebApiService?.OffThemeChange();
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const publicConfig = useSelector(store => store.app.publicConfig);
	const sessionStorageToken = isDevEnv ? sessionStorage.getItem('token') : undefined;
	useEffect(() => {
		if (token) {
			initStoreData();
		} else {
			if (sessionStorageToken) {
				dispatch(appSlice.actions.setToken(sessionStorageToken));
				try {
					WebSocketService.DoConnection(publicConfig.apiUrl, sessionStorageToken);
				} catch (error) {
					console.log(error);
					return;
				}
				return;
			}
			navigate(routes.main);
		}
	}, [token]);

	useEffect(() => {
		setItems(prev => {
			const same = prev.some(i => i.key === location.key);
			if (same && needAnimation) return prev;

			return [...prev, { key: location.key, node: outlet, direction }];
		});
		prevIdx.current = currentIdx;
	}, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleExitComplete = () => {
		setItems(prev => prev.slice(-1));

		setTimeout(() => {
			isFirstRender.current = false;
		}, 300);
	};

	useEffect(() => {
		if (!needAnimation) setItems(prev => prev.slice(-1));
	}, [location.pathname]);

	const variants = {
		start: {
			initial: { x: 0 },
			animate: { x: 0 },
			exit: { opacity: 1 },
		},
		forward: {
			initial: { x: '100%' },
			animate: { x: 0 },
			exit: { opacity: 0 },
		},
		backward: {
			initial: { x: '-100%' },
			animate: { x: 0 },
			exit: { opacity: 0 },
		},
	};

	return (
		<SnackbarProvider>
			<div className='App'>
				<AnimatePresence>
					{items.map((item, index) => (
						<motion.div
							className='block-animation'
							key={item.key}
							initial={needAnimation ? variants[item.direction].initial : false}
							animate={needAnimation ? variants[item.direction].animate : false}
							exit={needAnimation ? variants[item.direction].exit : false}
							transition={{ duration: 0.2, ease: 'easeInOut' }}
							onAnimationComplete={handleExitComplete}
							style={{
								zIndex: index,
							}}
						>
							<PWAInstalHeader installPWARef={installPWARef}>
								<PageKeyProvider value={item.key}>{item.node}</PageKeyProvider>
							</PWAInstalHeader>
						</motion.div>
					))}
				</AnimatePresence>
				<RootModals />
			</div>
		</SnackbarProvider>
	);
}

export default App;
